<script setup lang="ts">
import { onMounted, PropType, Ref, ref, watch } from "vue";
import AdminUnits from "./AdminUnits.vue";
import { Country, type FormPerson, Nationality, NIRAPerson } from "./types";
import api from "@/app/services/api.ts";
import type { AxiosResponse } from "axios";
import { ninCalculator } from "@/composables/ninCalculator.ts";
import { dateFormat } from "@/commons/composables/utilities.ts";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

const props = defineProps({
  active: Boolean,
  modelValue: {
    type: Object as PropType<FormPerson>,
  },
  data: {
    type: Object as PropType<FormPerson>,
  },
})

const nira = ninCalculator()
const countries:Ref<Country[]> = ref([])
const nationalities:Ref<Nationality[]> = ref([])

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormPerson): void
}>()

const individual = ref<FormPerson>(
  {
    givenName: '',
    surname: '',
    middleNames: '',
    gender: '',
    dateOfBirth: '',
    nationalityId: '',
    idType: '',
    idNumber: '',
    email: '',
    phone: '',
    countryId:'',
    address: {
      districtId: '',
      countyId: '',
      subCountyId: '',
      parishId: '',
      villageId: '',
      description: ""
    },
  },
)

onMounted(() => {
  fetchCountries()
  fetchNationalities()
})

watch(
  individual,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { deep: true },
)

watch(() => props.data, () => {
  individual.value = props.data ?? individual.value
})


const ninPerson = ref<NIRAPerson|undefined>()
const isSearching = ref(false)

function verifyId(){
  if(individual.value.idType != "national_id"){
    return
  }
  isSearching.value = true
  let payload = {
    nin: individual.value.idNumber,
    reference: nira.getNinReference()
  }
  return api
      .post(`/integration/api/nira`, payload)
      .then((response: AxiosResponse<NIRAPerson>) => {
        ninPerson.value = response.data

        individual.value.givenName = ninPerson.value.givenNames
        individual.value.middleNames = ninPerson.value.middleName || ""
        individual.value.surname = ninPerson.value.surname
        individual.value.dateOfBirth = dateFormat(ninPerson.value.dateOfBirth || "")
        individual.value.gender = ninPerson.value.gender == 'M' ? 'male' : 'female'
      })
      .finally(() => {
        isSearching.value = false
      })
}

const fetchNationalities = async () => {
  return api
      .get(`/configuration/nationality/list`)
      .then((response: AxiosResponse<Nationality[]>) => {
        const raw = Array.isArray(response.data) ? response.data : (response.data as any).data ?? []
        nationalities.value = raw.filter((n: any) => n != null)
      })
}

const fetchCountries = async () => {
  return api
    .get(`/configuration/address/country`)
      .then((response: AxiosResponse<Country[]>) => {
        const raw = Array.isArray(response.data) ? response.data : (response.data as any).data ?? []
        countries.value = raw.filter((n: any) => n != null)
      })
}

const errors = ref({
  idType: '',
  idNumber: '',
  nationalityId: '',
  countryId: '',
  givenName: '',
  surname: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  phone: '',
})

function validateIdType() {
  if (!individual.value.idType) {
    errors.value.idType = 'ID Type is required'
  } else {
    errors.value.idType = ''
  }
}

function validateIdNumber() {
  if (!individual.value.idNumber) {
    errors.value.idNumber = 'ID Number is required'
  } else {
    errors.value.idNumber = ''
  }
}

function validateNationalityId() {
  if (!individual.value.nationalityId) {
    errors.value.nationalityId = 'Nationality is required'
  } else {
    errors.value.nationalityId = ''
  }
}


function validateCountryId() {
  if (!individual.value.nationalityId) {
    errors.value.countryId = 'Country of Origin is required'
  } else {
    errors.value.countryId = ''
  }
}

function validateGivenName() {
  if (!individual.value.givenName) {
    errors.value.givenName = 'Given Name is required'
  } else {
    errors.value.givenName = ''
  }
}

function validateSurname() {
  if (!individual.value.surname) {
    errors.value.surname = 'Surname is required'
  } else {
    errors.value.surname = ''
  }
}

function validateGender() {
  if (!individual.value.gender) {
    errors.value.gender = 'Gender is required'
  } else {
    errors.value.gender = ''
  }
}

function validateDateOfBirth() {
  if (!individual.value.dateOfBirth) {
    errors.value.dateOfBirth = 'Date of Birth is required'
  } else {
    errors.value.dateOfBirth = ''
  }
}

function validateEmail() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!individual.value.email) {
    errors.value.email = 'Email is required'
  } else if (!emailRegex.test(individual.value.email)) {
    errors.value.email = 'Please enter a valid email address'
  } else {
    errors.value.email = ''
  }
}

function validatePhone() {
  const phoneRegex = /^(\+?256|0)[1-9]\d{8}$/
  if (!individual.value.phone) {
    errors.value.phone = 'Phone number is required'
  } else if (!phoneRegex.test(individual.value.phone.replace(/\s/g, ''))) {
    errors.value.phone = 'Please enter a valid phone number (e.g. 07XXXXXXXX or +256XXXXXXXXX)'
  } else {
    errors.value.phone = ''
  }
}

watch(() => individual.value.idType,
    () => {
      if (individual.value.idType == 'national_id') {
        fetchCountries()
      }
    },
    { deep: true })

function checkNationality() {
  if (individual.value.nationalityId?.toLowerCase().includes('Ugandan')) {
    individual.value.idType = 'national_id'
  } else {
    individual.value.idType = ''
  }
}
</script>

<template>
  <div class="flex flex-col space-y-3">
    <div class="border border-gray-200 rounded-lg p-4">
      <div class="grid grid-cols-2 gap-3">

        <div class="col-span-2">
          <div class="grid grid-cols-1 gap-3">
            <!-- Country -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-earth-africa text-gray-400 mr-1"></i>
                Nationality <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <select
                  v-model="individual.nationalityId"
                  required
                  @blur="validateNationalityId"
                  @change="checkNationality"
                  :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed', errors.nationalityId ? 'border-red-500' : 'border-gray-300']"
              >
                <option
                    v-for="(nationality, idx) in nationalities"
                    :key="idx"
                    :value="nationality.name">
                  {{ nationality.name }}
                </option>
              </select>
              <p v-if="errors.nationalityId" class="mt-1 text-xs text-red-600">{{ errors.nationalityId }}</p>
            </div>
          </div>
        </div>

        <!-- ID Type & Number Row -->
        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-3">
            <!-- ID Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-id-badge text-gray-400 mr-1"></i>
                ID Type <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <select
                v-model="individual.idType"
                required
                @blur="validateIdType"
                @change="validateIdType"
                :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed', errors.idType ? 'border-red-500' : 'border-gray-300']"
              >
                <option value="">Select ID Type</option>
                <option value="national_id" v-if="individual.nationalityId == 'Ugandan'">
                  National ID
                </option>
                <option value="passport" v-if="individual.nationalityId != 'Ugandan'">
                  Passport
                </option>
                <option value="alien_id" v-if="individual.nationalityId != 'Ugandan'">
                  Alien ID
                </option>
              </select>
              <p v-if="errors.idType" class="mt-1 text-xs text-red-600">{{ errors.idType }}</p>
            </div>

            <!-- ID Number -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-hashtag text-gray-400 mr-1"></i>
                ID Number <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <div class="flex gap-2">
                <input
                    v-model="individual.idNumber"
                    @change="verifyId()"
                    @keyup.enter="verifyId()"
                    @blur="validateIdNumber"
                    @input="validateIdNumber"
                    :disabled="!active"
                    type="text"
                    required
                    placeholder="Enter ID number"
                    :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.idNumber ? 'border-red-500' : 'border-gray-300']"/>
                <span class="lds-blue-ring" v-if="isSearching">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </span>
              </div>
              <p v-if="errors.idNumber" class="mt-1 text-xs text-red-600">{{ errors.idNumber }}</p>
            </div>
          </div>
        </div>

        <!-- Names Row -->
        <div class="col-span-2">
          <div class="grid grid-cols-3 gap-3">
            <!-- Given Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Given Name <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.givenName"
                type="text"
                required
                :disabled="individual.idType == 'national_id'"
                placeholder="Enter given name"
                @blur="validateGivenName"
                @input="validateGivenName"
                :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.givenName ? 'border-red-500' : 'border-gray-300']"
              />
              <p v-if="errors.givenName" class="mt-1 text-xs text-red-600">{{ errors.givenName }}</p>
            </div>

            <!-- Surname -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Surname <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.surname"
                type="text"
                required
                :disabled="individual.idType == 'national_id'"
                placeholder="Enter surname"
                @blur="validateSurname"
                @input="validateSurname"
                :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.surname ? 'border-red-500' : 'border-gray-300']"
              />
              <p v-if="errors.surname" class="mt-1 text-xs text-red-600">{{ errors.surname }}</p>
            </div>

            <!-- Middle Names -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Middle Names
              </label>
              <input
                v-model="individual.middleNames"
                type="text"
                :disabled="individual.idType == 'national_id'"
                placeholder="Enter middle names (optional)"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>

        <!-- Gender -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-venus-mars text-gray-400 mr-1"></i>
            Gender <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <select
            v-model="individual.gender"
            required
            :disabled="individual.idType == 'national_id'"
            @blur="validateGender"
            @change="validateGender"
            :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.gender ? 'border-red-500' : 'border-gray-300']"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <p v-if="errors.gender" class="mt-1 text-xs text-red-600">{{ errors.gender }}</p>
        </div>

        <!-- Date of Birth -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-calendar text-gray-400 mr-1"></i>
            Date of Birth <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <VueDatePicker
              v-model="individual.dateOfBirth"
              format="dd-MM-yyyy"
              model-type="format"
              :disabled="individual.idType == 'national_id'"
              auto-apply
              :max-date="new Date(new Date().setFullYear(new Date().getFullYear() - 18))"
              :min-date="new Date(new Date().setFullYear(new Date().getFullYear() - 150))"
              @update:model-value="validateDateOfBirth"
          ></VueDatePicker>
          <p v-if="errors.dateOfBirth" class="mt-1 text-xs text-red-600">{{ errors.dateOfBirth }}</p>
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
            Email <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <input
            v-model="individual.email"
            type="email"
            :disabled="!active"
            required
            placeholder="Enter email"
            @blur="validateEmail"
            @input="validateEmail"
            :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.email ? 'border-red-500' : 'border-gray-300']"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-600">{{ errors.email }}</p>
        </div>

        <!-- Phone -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
            Phone <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <PhoneInput
              v-model="individual.phone"
              label="Phone"
              defaultCountry="+256"
              placeholder="Enter phone number"
              :required="true"
              size="sm"
          />
          <p v-if="errors.phone" class="mt-1 text-xs text-red-600">{{ errors.phone }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-earth-africa text-gray-400 mr-1"></i>
            Country of Residence <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <select
              v-model="individual.countryId"
              required
              @blur="validateCountryId"
              :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed', errors.nationalityId ? 'border-red-500' : 'border-gray-300']"
          >
            <option
                v-for="(country, idx) in countries"
                :key="idx"
                :value="country.id">
              {{ country.name }}
            </option>
          </select>
          <p v-if="errors.countryId" class="mt-1 text-xs text-red-600">{{ errors.countryId }}</p>
        </div>

        <!-- Address -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fa-solid fa-map-marker-alt text-gray-400 mr-1"></i>
            Address <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <AdminUnits :active="active" :isUganda="countries.find(c => c.id == individual.countryId)?.name == 'Uganda'"
                      v-model="individual.address"
                      :data="individual.address" />
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped></style>
